import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AxiosError } from "axios";

import {
  TFunction,
  clearLocalStorageFieldFilterOptions,
  doRangesOverlap,
  getLocalStorageShowFieldRecommendations,
  isFieldEnrolled,
  normalizeDiacritics,
  triggerTrengo,
  useAddFarmSubmission,
  useAddFieldSimulation,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetAllFieldsRecommendations,
  useGetAllUserVerificationFarmTasks,
  useGetCarbonProgramByFarmId,
  useGetDataSubmissionDates,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldSimulations,
  useGetFieldsByFarmId,
  useGetVerificationEditingRequests,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import { ACCOUNTING_METHODOLOGY, CROP_TYPE_FCPO, CropItemData, PRACTICE_SLUG, VIEW_TYPE } from "models";

import {
  Button,
  Flex,
  FlexPageSpin,
  HideFeature,
  InputSearch,
  LazyLoadImage,
  MainPage,
  MetaData,
  RecommendationSwitch,
  Tag,
  Message as message,
} from "components";

import { AllFieldsTimeline } from "pages/app/all-field-view/components/AllFieldsTimeline";
import { DataSubmissionBar } from "pages/app/all-field-view/components/DataSubmissionBar";
import { DATA_STATUS, FieldFilterOptions } from "pages/app/all-field-view/components/FieldFilterOptions";
import { SubmissionSuccessModal } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { useMissingDataNavigation } from "pages/app/all-field-view/hooks/useMissingDataNavigation";
import { useScrollPositionPersistence } from "pages/app/all-field-view/hooks/useScrollPositionPersistence";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { createCropItems, getEnrolledFieldsCount, getFarmOpenTasksCount } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { CropMixComposer } from "pages/app/crop-mix-composer/CropMixComposer.tsx";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { FieldTimelineSimulationsOverlay } from "pages/app/field-timeline-simulations/FieldTimelineSimulationsOverlay";
import { TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline";
import { InterventionImpactPanel } from "pages/app/field-timeline/components/InterventionImpactPanel";
import { InventoryImpactPanel } from "pages/app/field-timeline/components/InventoryImpactPanel";
import { createPlaceholderItems } from "pages/app/field-timeline/utils/PlaceholderUtils";
import { getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils";
import { createRecommendations } from "pages/app/field-timeline/utils/RecommendationUtils";
import { RedzoneConfirmationDrawer } from "pages/app/fields/components/RedzoneConfirmationDrawer";

const HoldingScreenComponent = ({ t }: { t: TFunction }) => {
  const [trengo, setTrengo] = useState(false);

  useEffect(() => {
    if (window.Trengo) {
      window.Trengo.on_close = () => {
        setTrengo(false);
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex align="center" className="all-field-coming-soon container" data-testid="all-field-view-page-container" gap="3rem" vertical>
      <Flex className="hero-container" gap="1rem" justify="center">
        <div className="hero-explainer">
          <Tag className="margin-bottom-100" colour="yellow" text={t("comingSoon.tag")} />
          <h1 className="hero">{t("comingSoon.title")}</h1>
          <p className="paragraph-xl">{t("comingSoon.explainer")}</p>
        </div>
        <LazyLoadImage alt="hello" className="hero-image" src="/images/homepage/homepage_video.jpg" />
      </Flex>
      <Flex align="flex-end" className="list-container" justify="space-between">
        <div>
          <h1>{t("comingSoon.listHeader")}</h1>
          <ul>
            <li>{t("comingSoon.listItem1")}</li>
            <li>{t("comingSoon.listItem2")}</li>
            <li>{t("comingSoon.listItem3")}</li>
          </ul>
        </div>
        <Button style={{ maxWidth: "10rem" }} type="primary" onClick={() => triggerTrengo("email", trengo, setTrengo)}>
          {t("comingSoon.contact")}
        </Button>
      </Flex>
    </Flex>
  );
};

export const AllFieldView = () => {
  // loading practices.json here even though it's not used
  // this file is large and causes funky page behaviour if loaded later
  // this way, it's already loaded and available for the client prior to user action
  const { t } = useTranslation(["allFieldView", "fieldTimeline", "practices"]);
  const location = useLocation();
  const navigate = useNavigate();

  const { currentFarm } = useGetFarms();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(currentFarm?.id ?? "");
  const { data: activeFarmReportingYear, isLoading: isFarmReportingYearLoading } = useGetFarmReportingYear(currentFarm?.id || null);

  const { data: fields, isFetching: isFetchingFields } = useGetFieldsByFarmId({ farmId: currentFarm?.id ?? "" });
  const {
    data: carbonProgramOutcomes,
    progress,
    isLoading: isLoadingCarbonProgramOutcomes,
  } = useGetAllCarbonProgramOutcomes(currentFarm?.id, currentFarm?.carbonProgram);
  const { data: allFieldsPractices, isLoading: isLoadingAllFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: verificationFarmTasks } = useGetAllUserVerificationFarmTasks(currentFarm?.id);
  const { data: dataSubmissionDates } = useGetDataSubmissionDates();

  // Applied filter state (what's currently filtering the fields)
  const [appliedCropIds, setAppliedCropIds] = useState<string[]>([]);
  const [appliedDataStatus, setAppliedDataStatus] = useState<DATA_STATUS>(DATA_STATUS.ALL);
  const [appliedYears, setAppliedYears] = useState<number[]>([]);
  const [fieldsInView, setFieldsInView] = useState<string[]>([]);
  const [filteredFieldIds, setFilteredFieldIds] = useState<string[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilterOptionsCount, setSelectedFilterOptionsCount] = useState(0);
  const [showRecommendations, setShowRecommendations] = useState(!!getLocalStorageShowFieldRecommendations());
  const [showRedzoneConfirmationDrawer, setShowRedzoneConfirmationDrawer] = useState<boolean>(false);
  const [showSimulations, setShowSimulations] = useState<boolean>(false);
  const [showCropMixComposer, setShowCropMixComposer] = useState<boolean>(false);
  const [simulationFieldId, setSimulationFieldId] = useState<string>();
  const [showSubmissionSuccessModal, setShowSubmissionSuccessModal] = useState<boolean>(false);

  const { selectedRecommendationItemOnTimeline } = useCropMixComposerContext();
  const { getItemsWithMissingDataByFieldIdMultiView } = useMissingDataNavigation();
  const { data: verificationEditingRequests } = useGetVerificationEditingRequests({
    farmId: currentFarm?.id ?? "",
  });

  // TODO pass actual simulation name
  const { data: fieldSimulations, isPending: isPendingFieldSimulations } = useGetFieldSimulations({ fieldId: simulationFieldId });
  const { mutate: addFieldSimulation } = useAddFieldSimulation({ fieldId: simulationFieldId || "", name: "First Simulation" });
  const { data: cropRecommendationsByFieldId, isFetching: isFetchingCropRecommendations } = useGetAllFieldsRecommendations(
    getLocalStorageShowFieldRecommendations() ? fieldsInView : []
  );

  const { mutateAsync: submitData } = useAddFarmSubmission({ farmId: currentFarm?.id ?? "" });
  const { isDataSubmitted, isDataSubmissionReopened, isSubmissionFeatureEnabled } = useSubmissionStatus(activeFarmReportingYear);

  const { scrollContainerRef, saveScrollPosition } = useScrollPositionPersistence(
    `field-list-scroll-${currentFarm?.id || "default"}`,
    !isFetchingFields && !!fields?.length
  );

  const isSimulationsPath = location.pathname.includes("/simulations");
  const isCropMixComposerPath = location.pathname.includes("/cmc");

  // intentionally checking for false. If a farm is unenrolled, show the empty program state, not the holding screen
  const isDisabledProgramFarm = currentFarm?.carbonProgram?.isEnabled === false;
  // need this line since 'isDisabledProgramFarm' is not accounting for null or undefined state
  const isEnabledProgramFarm = !!currentFarm?.carbonProgram?.isEnabled;
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const openVerificationFarmTasksCount = (verificationFarmTasks || []).filter((task) => !task.completedAt).length;
  const enrolledFieldsCount = getEnrolledFieldsCount(fields ?? [], carbonProgramOutcomes ?? {}, carbonProgram || null);

  const queryVariants = useMemo(() => normalizeDiacritics(searchQuery), [searchQuery]);
  const filteredFields = useMemo(() => {
    const availableFields = filteredFieldIds === null ? fields : fields?.filter((field) => filteredFieldIds.includes(field.id));
    return (
      availableFields?.filter((field) => {
        const nameVariants = normalizeDiacritics(field.name);
        return queryVariants.some((queryVariant: string) => nameVariants.some((nameVariant: string) => nameVariant.includes(queryVariant)));
      }) ?? []
    );
  }, [fields, queryVariants, filteredFieldIds]);

  const activeField = fields?.find((field) => field.id === selectedRecommendationItemOnTimeline?.group);

  const timelineItems = useMemo(() => {
    if (!carbonProgramOutcomes || !carbonProgram || !fields || fields.length === 0 || isLoadingAllFieldsPractices || !activeFarmReportingYear) return [];

    return Object.entries(allFieldsPractices || {}).reduce((acc, [fieldId, fieldPracticeResponse]) => {
      if (!fields.find((field) => field.id === fieldId) || !fieldPracticeResponse) return acc;

      const cropBaselineYears = getBaselineYearsByPracticeType(carbonProgramOutcomes[fieldId])[PRACTICE_SLUG.CROPS] ?? [];
      const isEnrolled = isFieldEnrolled(carbonProgramOutcomes?.[fieldId]);

      const cropItems = createCropItems(fieldPracticeResponse, fieldId, cropBaselineYears, isEnrolled, t);
      const cropPlaceholderItems = createPlaceholderItems(
        fieldPracticeResponse,
        cropBaselineYears,
        isEnrolled,
        carbonProgram,
        activeFarmReportingYear,
        fieldId,
        verificationEditingRequests
      );

      // create CMC placeholder cards starting from cover crops placeholder cards for years with a main crop
      const coverCropPlaceholderItems = cropPlaceholderItems.filter(
        (placeholder) =>
          placeholder.data.cropType === CROP_TYPE_FCPO.COVER &&
          cropItems.some((crop) => crop.data.cropType === CROP_TYPE_FCPO.MAIN && crop.data.year === placeholder.data.year)
      );
      const recommendationPlaceholderItems = cropRecommendationsByFieldId?.[fieldId]
        ? createRecommendations(cropRecommendationsByFieldId[fieldId] ?? [], coverCropPlaceholderItems, fieldId, fieldId)
        : [];

      if (showRecommendations && recommendationPlaceholderItems.length > 0) {
        const filteredPlaceholderItems = cropPlaceholderItems.filter((crop) => {
          if (crop.data?.cropType === CROP_TYPE_FCPO.MAIN) return true;

          const overlappingPlaceholder = recommendationPlaceholderItems.some((recommendation) =>
            doRangesOverlap(recommendation.start_time, recommendation.end_time, crop.start_time, crop.end_time)
          );
          return !overlappingPlaceholder;
        });

        acc.push(...cropItems, ...filteredPlaceholderItems, ...recommendationPlaceholderItems);
        return acc;
      }

      acc.push(...cropItems, ...cropPlaceholderItems);
      return acc;
    }, [] as TimelineCalendarItemsProps<CropItemData>[]);
  }, [
    activeFarmReportingYear,
    carbonProgramOutcomes,
    carbonProgram,
    fields,
    isLoadingAllFieldsPractices,
    allFieldsPractices,
    t,
    cropRecommendationsByFieldId,
    showRecommendations,
    verificationEditingRequests,
  ]);

  const itemsWithMissingDataByFieldId = getItemsWithMissingDataByFieldIdMultiView(
    fields ?? [],
    carbonProgramOutcomes,
    timelineItems,
    carbonProgram || null,
    verificationEditingRequests,
    dataSubmissionDates,
    isSubmissionFeatureEnabled
  );

  const { fieldsWithOpenTasksCount, openTasksCount } = getFarmOpenTasksCount(itemsWithMissingDataByFieldId, verificationEditingRequests ?? []);

  const handleSimulationsClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowSimulations(true);

    // remove focus from the button after the function runs to remove the css :focus styling
    if (e.currentTarget instanceof HTMLButtonElement) {
      e.currentTarget.blur();
    }
  };

  const handleClearFilters = () => {
    setFilteredFieldIds(null);
    setSearchQuery("");
    setSelectedFilterOptionsCount(0);
    setAppliedCropIds([]);
    setAppliedYears([]);
    setAppliedDataStatus(DATA_STATUS.ALL);
    clearLocalStorageFieldFilterOptions();
  };

  useEffect(() => {
    if (!simulationFieldId || isPendingFieldSimulations) return;
    if (!fieldSimulations?.length) addFieldSimulation();
  }, [fieldSimulations, simulationFieldId, isPendingFieldSimulations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname.includes("/cmc") && !selectedRecommendationItemOnTimeline) {
      const basePath = location.pathname.split("/cmc").slice(0, -1).join("/");
      navigate(basePath);
    }
  }, [location.pathname, navigate, selectedRecommendationItemOnTimeline]);

  const navigationCrumbs = {
    crumbs: [
      { name: t("global:header.home"), link: "/home" },
      { name: currentFarm?.name || t("modules:navigation.overview.farm") },
      { name: t("fieldTimeline:header.fieldManagementCrumb") },
    ],
    additionalData: <></>,
  };

  const fieldSizes =
    fields?.reduce((fieldSizes: Record<string, number>, field) => {
      fieldSizes[field.id] = field.size ?? 0;
      return fieldSizes;
    }, {}) || {};

  const handleSubmitClick = async () => {
    if (!currentFarm?.id || !carbonProgramOutcomes) return;

    try {
      const { success } = await submitData({ farmId: currentFarm.id });
      if (success) {
        setShowSubmissionSuccessModal(true);
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400 && error.response?.data?.message) {
        message({ content: t("allFieldView:submission.errorMessage", { message: error.response?.data?.message }), icon: "error" });
      } else {
        message({ content: t("allFieldView:submission.error"), icon: "error" });
      }
      // eslint-disable-next-line no-console
      console.error("Submission failed:", error);
    }
  };

  const fieldsWithOutstandingRedzones = fields?.filter((field) => field.redzones?.some((redzone) => redzone.confirmed === null)) ?? [];
  const shouldShowSubmissionSuccessModalContent = isDataSubmitted && !isDataSubmissionReopened;
  const shouldShowSubmissionBar = isSubmissionFeatureEnabled && isEnabledProgramFarm && enrolledFieldsCount > 0;

  const handleOpenTasksClick = useCallback(() => {
    // TODO: Implement SO-778 - Open Task Flow
    if (fieldsWithOutstandingRedzones.length > 0) setShowRedzoneConfirmationDrawer(true);
  }, [fieldsWithOutstandingRedzones.length]);

  return isDisabledProgramFarm ? (
    <MainPage fullWidth loading={isFetchingFields} pageName="all-field-page" navigationCrumbs={navigationCrumbs} showMobileAlert>
      <HoldingScreenComponent t={t} />
    </MainPage>
  ) : (
    <MainPage fullWidth loading={isFetchingFields} pageName="all-field-page" navigationCrumbs={navigationCrumbs} showMobileAlert>
      <MetaData title={t("allFieldView")} />
      <Flex vertical>
        <Flex className="all-field-view container" data-testid="all-field-view-page-container" justify="space-between">
          <Flex vertical className={`main-container${shouldShowSubmissionBar ? " with-submission-bar" : ""}`} ref={scrollContainerRef}>
            <Flex justify="space-between" align="center">
              <Flex className="search-container" align="center">
                <InputSearch
                  dataTestId="filter-fields"
                  className="search-input"
                  isSmallVariant
                  label={t("search")}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={searchQuery}
                />
              </Flex>
              <Flex className="filters-container" justify="space-between" align="center" gap="1rem">
                <Flex align="center">
                  <FieldFilterOptions
                    fields={fields ?? []}
                    filteredFieldIds={filteredFieldIds}
                    setFilteredFieldIds={setFilteredFieldIds}
                    selectedFilterOptionsCount={selectedFilterOptionsCount}
                    setSelectedFilterOptionsCount={setSelectedFilterOptionsCount}
                    appliedCropIds={appliedCropIds}
                    appliedYears={appliedYears}
                    appliedDataStatus={appliedDataStatus}
                    setAppliedCropIds={setAppliedCropIds}
                    setAppliedYears={setAppliedYears}
                    setAppliedDataStatus={setAppliedDataStatus}
                    farmId={currentFarm?.id || ""}
                    handleClearFilters={handleClearFilters}
                    itemsWithMissingDataByFieldId={itemsWithMissingDataByFieldId}
                  />
                </Flex>
                <Flex className="recommendation-toggle-container" align="center" gap="1rem">
                  <RecommendationSwitch
                    showRecommendations={showRecommendations}
                    setShowRecommendations={setShowRecommendations}
                    isFetchingCropRecommendations={isFetchingCropRecommendations}
                  />
                  {!!fields?.length && !isInventoryProgram && isEnabledProgramFarm && (
                    <Button type="primary" className="simulation-toggle" onClick={handleSimulationsClick}>
                      {t("fieldTimeline:header.simulationButtonText")}
                    </Button>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <div className="timeline-container">
              {isFarmReportingYearLoading ? (
                <FlexPageSpin />
              ) : (
                <AllFieldsTimeline
                  allFields={fields ?? []}
                  filteredFields={filteredFields}
                  allFieldsPractices={allFieldsPractices || {}}
                  itemsWithMissingDataByFieldId={itemsWithMissingDataByFieldId}
                  timelineItems={timelineItems}
                  setSimulationFieldId={setSimulationFieldId}
                  showSimulationFieldSelectionModal={(isSimulationsPath || showSimulations) && !simulationFieldId}
                  setShowSimulations={setShowSimulations}
                  carbonProgramOutcomesResponse={carbonProgramOutcomes}
                  isLoadingCarbonProgramOutcomes={isLoadingCarbonProgramOutcomes}
                  fieldsInView={fieldsInView}
                  setFieldsInView={setFieldsInView}
                  cropRecommendationsByFieldId={cropRecommendationsByFieldId}
                  farmFieldsCount={fields?.length || 0}
                  hasAppliedFilters={!!searchQuery || selectedFilterOptionsCount > 0}
                  handleClearFilters={handleClearFilters}
                  onFieldNavigate={saveScrollPosition}
                  verificationEditingRequests={verificationEditingRequests}
                  reportingYear={activeFarmReportingYear!} // here we know that activeFarmReportingYear is not null because we checked it in the if statement
                  shouldShowIndividualFieldPayouts={!shouldShowSubmissionSuccessModalContent}
                />
              )}
            </div>
          </Flex>
          <HideFeature
            hide={
              !currentFarm?.carbonProgram?.id || currentFarm?.fieldCount === 0 || !currentFarm?.carbonProgram?.platformFeatures?.fieldManagement?.impactPanel
            }
            fallback={null}
          >
            <Flex className={`all-field-view sidebar${shouldShowSubmissionBar ? " with-submission-bar" : ""}`} vertical={true}>
              {isFarmReportingYearLoading ? (
                <FlexPageSpin />
              ) : (
                <>
                  {currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? (
                    <InventoryImpactPanel
                      carbonProgramOutcomes={carbonProgramOutcomes}
                      progress={progress}
                      fieldSizes={fieldSizes}
                      hidePayouts={currentFarm.carbonProgram.hidePayouts}
                      shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
                      reportingYearFromProps={activeFarmReportingYear!} // here we know that activeFarmReportingYear is not null because we checked it in the if statement
                    />
                  ) : (
                    <InterventionImpactPanel
                      carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
                      progress={progress}
                      fieldSizes={fieldSizes}
                      viewType={VIEW_TYPE.ALL_FIELDS}
                      hidePayouts={currentFarm?.carbonProgram?.hidePayouts}
                      shouldShowSubmissionSuccessModalContent={shouldShowSubmissionSuccessModalContent}
                      reportingYearFromProps={activeFarmReportingYear!} // here we know that activeFarmReportingYear is not null because we checked it in the if statement
                    />
                  )}
                </>
              )}
            </Flex>
          </HideFeature>
        </Flex>
        {shouldShowSubmissionBar && (
          <>
            <DataSubmissionBar
              enrolledFieldsCount={enrolledFieldsCount}
              fieldsWithMissingTasksCount={fieldsWithOpenTasksCount}
              hasMissingDocuments={openVerificationFarmTasksCount > 0}
              openTasksCount={openTasksCount}
              outstandingRedzoneCount={fieldsWithOutstandingRedzones.length}
              onOpenTasksClick={handleOpenTasksClick}
              onSubmitClick={handleSubmitClick}
            />
            <RedzoneConfirmationDrawer
              fields={fieldsWithOutstandingRedzones}
              visible={showRedzoneConfirmationDrawer}
              onClose={() => setShowRedzoneConfirmationDrawer(false)}
              onConfirm={() => {}}
            />
          </>
        )}
      </Flex>
      {!!simulationFieldId && activeFarmReportingYear && (
        <FieldTimelineSimulationsOverlay
          visible={isSimulationsPath || showSimulations}
          setShowSimulations={setShowSimulations}
          carbonProgram={currentFarm?.carbonProgram || null}
          fieldId={simulationFieldId}
          setSimulationFieldId={setSimulationFieldId}
          isFetchingCropRecommendations={isFetchingCropRecommendations}
        />
      )}
      <CropMixComposer
        visible={showCropMixComposer || isCropMixComposerPath}
        setShowCropMixComposer={setShowCropMixComposer}
        regionId={activeField?.regionId}
        openInDrawer
        fieldId={activeField?.id}
      />
      {showSubmissionSuccessModal && shouldShowSubmissionSuccessModalContent && activeFarmReportingYear && (
        <SubmissionSuccessModal
          carbonProgramOutcomes={carbonProgramOutcomes ?? null}
          farmId={currentFarm?.id ?? ""}
          visible={showSubmissionSuccessModal}
          onClose={() => setShowSubmissionSuccessModal(false)}
        />
      )}
    </MainPage>
  );
};
